import dayjs from 'dayjs';
import type { PurchaseOrderDTO, PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import { formatAmountInWords } from './amountInWords';
import { headerProjectLabel, linesCarryProjects } from './projectDisplay';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE, formatDate } from '@/utils/format';
import { isEnvelopePoType } from '@/constants/purchaseOrderTypes';

export type PoReferenceRow = { label: string; value: string };

function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function formatPrintDate(value?: string | null): string {
  if (!value) return '';
  const safe = formatDate(value, { style: 'print_dmY' });
  return safe || dayjs(value).format('DD-MM-YYYY');
}

export function formatPoAmount(amount: unknown): string {
  return formatMoney(num(amount), PRINT_PLAIN_AMOUNT_PROFILE);
}

/** Match quotation print: `AED - Thirty Thousand And 00/100 Only`. */
export function formatPoAmountInWords(amount: number, currency: string): string {
  return formatAmountInWords(num(amount), currency);
}

function poProjectPrintLabel(po: PurchaseOrderDTO, items?: PurchaseOrderItemDTO[]): string {
  const lines = items ?? po.items ?? null;
  if (linesCarryProjects(lines)) return '';
  const label = headerProjectLabel(po, lines);
  return label === '—' ? '' : label;
}

export function formatPoReferenceRows(
  po: PurchaseOrderDTO,
  items?: PurchaseOrderItemDTO[],
  options?: { spotPurchaserName?: string | null }
): PoReferenceRow[] {
  const rows: PoReferenceRow[] = [];
  if (po.purchaseRequisition?.pr_number) {
    rows.push({ label: 'PR No.', value: po.purchaseRequisition.pr_number });
  } else if (po.pr_id) {
    rows.push({ label: 'PR', value: 'Linked' });
  }
  if (po.rfq_id) rows.push({ label: 'RFQ', value: 'Linked' });
  if (po.supplierInquiry?.inquiry_number) {
    rows.push({ label: 'Inquiry', value: po.supplierInquiry.inquiry_number });
  }
  if (po.quotation_reference) {
    rows.push({ label: 'Quotation ref.', value: po.quotation_reference });
  }
  const projectLabel = poProjectPrintLabel(po, items);
  if (projectLabel) {
    rows.push({ label: 'Project', value: projectLabel });
  }
  if (po.po_type && po.po_type !== 'Standard') {
    rows.push({ label: 'PO type', value: po.po_type });
  }
  if (po.parent_po_id && po.envelopeParent?.po_number) {
    rows.push({ label: 'Against', value: po.envelopeParent.po_number });
  } else if (po.parent_po_id) {
    rows.push({ label: 'Against', value: 'Parent envelope PO' });
  }
  if (isEnvelopePoType(po.po_type) && !po.parent_po_id) {
    rows.push({
      label: 'Note',
      value: 'Envelope agreement — receive against releases/call-offs only',
    });
  }
  if (po.procurement_method === 'MARKET_PURCHASE') {
    rows.push({ label: 'Procurement', value: 'Spot / Market Purchase' });
  }
  const purchaser = String(options?.spotPurchaserName || '').trim();
  if (purchaser) {
    rows.push({ label: 'Market purchaser', value: purchaser });
  }
  return rows;
}

export function lineHasDiscountColumns(items: PurchaseOrderItemDTO[]): boolean {
  return items.some((l) => num(l.discount_percentage) > 0 || num(l.discount_amount) > 0);
}

export function lineHasSpecColumn(items: PurchaseOrderItemDTO[]): boolean {
  return items.some((l) => (l.specifications ?? '').trim().length > 0);
}

export function lineHasDeliverySchedule(items: PurchaseOrderItemDTO[]): boolean {
  return items.some((l) => !!l.expected_delivery_date);
}

export function lineHasVatColumns(items: PurchaseOrderItemDTO[], poVatApplicable?: boolean): boolean {
  if (poVatApplicable === false) return false;
  return items.some(
    (l) => l.vat_applicable !== false && (num(l.vat_amount) > 0 || num(l.vat_percentage) > 0)
  );
}

export function lineTaxTypeLabel(
  vatPercentage: number | undefined,
  vatApplicable: boolean | undefined
): string {
  if (vatApplicable === false) return 'Exempt';
  if (num(vatPercentage) === 0) return 'VAT 0%';
  return `VAT ${num(vatPercentage)}%`;
}

/** Print totals: honor line-level VAT exempt even when header vat_amount is stale. */
export function computePoPrintVatSummary(
  po: PurchaseOrderDTO,
  items: PurchaseOrderItemDTO[]
): { exempt: boolean; vatAmount: number; vatLabel: string } {
  const lineVatSum = items.reduce((sum, line) => {
    if (line.vat_applicable === false) return sum;
    return sum + num(line.vat_amount);
  }, 0);
  const allLinesExempt =
    items.length > 0 && items.every((line) => line.vat_applicable === false);
  const exempt = po.vat_applicable === false || allLinesExempt || lineVatSum === 0;
  const vatAmount = exempt ? 0 : lineVatSum > 0 ? lineVatSum : num(po.vat_amount);
  const vatLabel = exempt
    ? 'VAT :'
    : `VAT (${po.vat_percentage ?? 5}%) :`;
  return { exempt, vatAmount, vatLabel };
}

export type PoPrintCommercial = {
  gross: number;
  discount: number;
  discountLabel: string;
  after: number;
};

function lineGross(line: PurchaseOrderItemDTO): number {
  return line.line_total != null ? num(line.line_total) : num(line.ordered_quantity) * num(line.unit_price);
}

function lineDiscount(line: PurchaseOrderItemDTO): number {
  const gross = lineGross(line);
  const stored = line.discount_amount != null ? num(line.discount_amount) : 0;
  const fromPercent = (gross * num(line.discount_percentage)) / 100;
  return stored > 0 ? stored : fromPercent;
}

/**
 * Gross merchandise, line plus header discount, and amount after discount.
 * Stored grand_total is ignored so a printed 5% line discount cannot be left off the total.
 */
export function computePoPrintCommercial(
  po: PurchaseOrderDTO,
  items: PurchaseOrderItemDTO[]
): PoPrintCommercial {
  const summed = Math.round(items.reduce((sum, line) => sum + lineGross(line), 0) * 100) / 100;
  const gross = items.length > 0 && summed > 0 ? summed : num(po.subtotal);
  const lineDisc = Math.round(items.reduce((sum, line) => sum + lineDiscount(line), 0) * 100) / 100;
  const headerPct = num(po.discount_percentage);
  let headerDisc = Math.round(((gross * headerPct) / 100) * 100) / 100;
  if (items.length === 0 || (headerPct === 0 && lineDisc === 0)) {
    headerDisc = headerPct > 0 ? headerDisc : num(po.discount_amount);
  }
  const discount = Math.round((lineDisc + headerDisc) * 100) / 100;
  const headerOnly =
    headerPct > 0 && discount === Math.round(((gross * headerPct) / 100) * 100) / 100;
  return {
    gross,
    discount,
    discountLabel: headerOnly ? `Discount (${headerPct}%)` : 'Discount',
    after: Math.round((gross - discount) * 100) / 100,
  };
}

export function computePoPrintGrandTotal(
  po: PurchaseOrderDTO,
  items: PurchaseOrderItemDTO[],
  vatSummary: { exempt: boolean; vatAmount: number }
): number {
  const commercial = computePoPrintCommercial(po, items);
  const freight = num(po.freight_charges);
  const other = num(po.other_charges);
  const overhead = num(po.total_overhead);
  const vatAmount = vatSummary.exempt ? 0 : vatSummary.vatAmount;
  return Math.round((commercial.after + vatAmount + freight + other + overhead) * 100) / 100;
}

export function formatTrnDisplay(taxNumber?: string, vatNumber?: string): string {
  const raw = (taxNumber || vatNumber || '').trim();
  return raw.replace(/^TRN:\s*/i, '');
}

/** Supplier block on PO print: snapshot on PO first, else supplier master. */
export function formatSupplierPrintAddress(po: PurchaseOrderDTO): string {
  const snap = (po.supplier_address ?? '').trim();
  if (snap) return snap;
  const s = po.supplier;
  if (!s) return '';
  return [s.address_line1, s.address_line2, s.city, s.state, s.country, s.postal_code]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(', ');
}

export function formatSupplierPrintTrn(po: PurchaseOrderDTO): string {
  const s = po.supplier;
  return formatTrnDisplay(s?.tax_registration_number, s?.vat_number);
}
